import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
    ModalClass,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { from, of } from 'rxjs';
import { catchError, exhaustMap, mergeMap, switchMap, tap } from 'rxjs/operators';
import { EquipmentsListComponent } from '../../equipments-list/equipments-list.component';
import { InterimsLimitComponent } from '../../interims-limit/interims-limit.component';
import { EquipmentService } from '../../services/equipment/equipment.service';
import { NotificationService } from '../../services/notification/notification.service';
import { isInterimExistInEquipments } from '../../utilities/equipments.utilities';
import {
    getEquipmentsError,
    getEquipmentsSuccess,
    getEquipmentsWithMatSer,
    getEquipmentsWithOrder,
    navigateToForms,
    navigateToProtocols,
    showEquipmentsListModal,
    showInterimsLimitModal,
    showNoEquipmentsAvailableModal,
} from '../actions/protocols.action';

@Injectable({
    providedIn: 'root',
})
export class ProtocolsEffectsService {
    public constructor(
        private actions$: Actions,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly router: Router,
        private readonly modalService: BsModalService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly equipmentService: EquipmentService,
        private readonly notificationService: NotificationService,
        private readonly translate: TranslateService,
    ) {}

    public getEquipmentsWithMatSer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getEquipmentsWithMatSer),
            switchMap((action) => {
                const { materialNumber, serialNumber } = action;
                return this.equipmentService.getEquipmentsByMatSer(materialNumber, serialNumber).pipe(
                    mergeMap((response) => {
                        const data = response[0];
                        const interimProtocols = _.cloneDeep(data.InterimProtocolsSet.results);
                        const equipments = _.cloneDeep(data.EquipmentsSet.results);
                        // show info popup if there are no equipments available
                        if (equipments.length === 0) {
                            return [showNoEquipmentsAvailableModal(), getEquipmentsSuccess({ protocols: data })];
                        }
                        // show interims limit reached popup if opened interims >= interims limit
                        if (
                            interimProtocols.length >= this.configService.getMaxInterimsCount() &&
                            !isInterimExistInEquipments(interimProtocols, [{ ...equipments[0] }])
                        ) {
                            return [
                                showInterimsLimitModal({ interims: interimProtocols }),
                                getEquipmentsSuccess({ protocols: data }),
                            ];
                        }

                        this.notificationService.createDummyNotificationForFI(data);
                        return [
                            getEquipmentsSuccess({ protocols: data }),
                            navigateToForms({ orderNumber: equipments[0].OrderNumber }),
                        ];
                    }),
                    catchError((error) => {
                        this.log.error(
                            `ProtocolsEffects: getEquipmentsWithMatSer$: error in get equipments with material/serial number: ${JSON.stringify(
                                error,
                                Object.getOwnPropertyNames(error),
                            )}`,
                        );
                        return of(getEquipmentsError({ error }));
                    }),
                );
            }),
        ),
    );

    public getEquipmentsWithOrder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getEquipmentsWithOrder),
            switchMap((action) => {
                const orderNumber = action.orderNumber;
                return this.equipmentService.getEquipmentsByOrder(orderNumber).pipe(
                    mergeMap((response) => {
                        const data = response[0];
                        const interimProtocols = _.cloneDeep(data.InterimProtocolsSet.results);
                        const equipments = _.cloneDeep(data.EquipmentsSet.results);
                        // show info popup if there are no equipments available
                        if (equipments.length === 0) {
                            return [showNoEquipmentsAvailableModal(), getEquipmentsSuccess({ protocols: data })];
                        }
                        // show interims limit reached popup if opened interims >= interims limit
                        if (
                            interimProtocols.length >= this.configService.getMaxInterimsCount() &&
                            !isInterimExistInEquipments(interimProtocols, equipments)
                        ) {
                            return [
                                showInterimsLimitModal({ interims: interimProtocols }),
                                getEquipmentsSuccess({ protocols: data }),
                            ];
                        }

                        this.notificationService.createDummyNotificationForFI(data);
                        return [getEquipmentsSuccess({ protocols: data }), showEquipmentsListModal()];
                    }),
                    catchError((error) => {
                        this.log.error(
                            `ProtocolsEffects: getEquipmentsWithOrder$: error in get equipments with order number: ${JSON.stringify(
                                error,
                                Object.getOwnPropertyNames(error),
                            )}`,
                        );
                        return of(getEquipmentsError({ error }));
                    }),
                );
            }),
        ),
    );

    public navigateToForms$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(navigateToForms),
                exhaustMap((action) => {
                    const frontId = this.configService.getUserFrontId();
                    const maNum = '0001'; // task number: we'll have some dummy number as there is no role of tasks in FI
                    const qmNum = action.orderNumber;
                    return from(this.router.navigate(['./main/forms/', frontId, qmNum, maNum]));
                }),
            ),
        { dispatch: false },
    );

    public navigateToProtocols$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(navigateToProtocols),
                exhaustMap(() => {
                    return from(this.router.navigate(['./protocol-provisioning']));
                }),
            ),
        { dispatch: false },
    );

    public showEquipmentsListModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(showEquipmentsListModal),
                tap(() => {
                    this.modalService.show(EquipmentsListComponent, {
                        backdrop: 'static',
                        class: ModalClass.MODAL_LARGE,
                        ariaLabelledBy: 'equipments-list-modal-title',
                    });
                }),
            ),
        { dispatch: false },
    );

    public showNoEquipmentsAvailableModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(showNoEquipmentsAvailableModal),
                tap(() => {
                    this.modalService.show(YesNoModalDialogComponent, {
                        backdrop: 'static',
                        class: ModalClass.MODAL_MEDIUM,
                        initialState: {
                            model: new YesNoModalDialogModel(
                                `${this.translate.instant('NO_EQUIPMENTS_TITLE')}`,
                                `${this.translate.instant('NO_EQUIPMENTS_MESSAGE')}`,
                                `${this.translate.instant('NO_EQUIPMENTS_BUTTON_OK')}`,
                            ),
                        },
                        ariaLabelledBy: 'yes-no-modal-title',
                    });
                }),
            ),
        { dispatch: false },
    );

    public showInterimsLimitModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(showInterimsLimitModal),
                tap((action) => {
                    this.modalService.show(InterimsLimitComponent, {
                        backdrop: 'static',
                        class: ModalClass.MODAL_MEDIUM,
                        initialState: {
                            interims: action.interims,
                        },
                        ariaLabelledBy: 'interims-limit-modal-title',
                    });
                }),
            ),
        { dispatch: false },
    );
}
